import {
  healthResponseSchema,
  authMeResponseSchema,
  setupStatusSchema,
  memberDtoSchema,
  calendarDtoSchema,
  occurrenceDtoSchema,
  eventDtoSchema,
  listDtoSchema,
  listItemDtoSchema,
  feedPageSchema,
  feedActivityDtoSchema,
  feedCommentDtoSchema,
  conversationDtoSchema,
  messageDtoSchema,
  allowanceAccountDtoSchema,
  allowanceTxnDtoSchema,
  allowanceRuleDtoSchema,
  savingsGoalDtoSchema,
  gradeRewardDtoSchema,
  bankConfigDtoSchema,
  integrationsStatusSchema,
  immichAlbumDtoSchema,
  immichManageAlbumDtoSchema,
  immichAssetDtoSchema,
  tandoorMealDtoSchema,
  tandoorSyncResultSchema,
  tandoorRecipeDtoSchema,
  mealWishDtoSchema,
  birthdayDtoSchema,
  calendarSubscriptionDtoSchema,
  calendarPresetSchema,
  calendarFeedStatusSchema,
  calendarFeedCreatedSchema,
  taskTemplateDtoSchema,
  pointsAccountDtoSchema,
  pointsTxnDtoSchema,
  shiftPlanEntryDtoSchema,
  type HealthResponse,
  type MemberDto,
  type CalendarDto,
  type OccurrenceDto,
  type EventDto,
  type CreateEvent,
  type UpdateEvent,
  type CreateCalendar,
  type ListDto,
  type ListItemDto,
  type CreateList,
  type UpdateList,
  type CreateListItem,
  type UpdateListItem,
  type FeedPage,
  type FeedActivityDto,
  type FeedCommentDto,
  type CreatePoll,
  type SetupRequest,
  type CreateMember,
  type UpdateMember,
  type ConversationDto,
  type MessageDto,
  type CreateConversation,
  type AllowanceAccountDto,
  type AllowanceTxnDto,
  type AllowanceRuleDto,
  type SavingsGoalDto,
  type CreateTxn,
  type UpsertRule,
  type CreateGoal,
  type UpdateGoal,
  type GradeRewardDto,
  type BankConfigDto,
  type GradeBonus,
  type ImmichAlbumDto,
  type ImmichManageAlbumDto,
  type ImmichAssetDto,
  type TandoorMealDto,
  type TandoorSyncResult,
  type TandoorRecipeDto,
  type MealWishDto,
  type BirthdayDto,
  type CreateBirthday,
  type UpdateBirthday,
  type OccasionKind,
  type CalendarSubscriptionDto,
  type CalendarPreset,
  type CalendarFeedStatus,
  type CalendarFeedCreated,
  type SeriesScope,
  type CreateCalendarSubscription,
  type UpdateCalendarSubscription,
  type TaskTemplateDto,
  type CreateTaskTemplate,
  type UpdateTaskTemplate,
  type CreditTask,
  type PointsAccountDto,
  type PointsTxnDto,
  type ShiftPlanEntryDto,
  type ShiftCode,
  loginResponseSchema,
  oidcStatusSchema,
  type OidcStatus,
} from '@familynest/shared'
import { appReleaseDtoSchema, latestReleaseResponseSchema, releaseListResponseSchema, type AppReleaseDto, type LatestReleaseResponse } from '@familynest/shared'
import { z } from 'zod'
import { apiFetch, isNative, setToken } from './config'

/** Basispfad je Anlass-Art (Geburtstage / Jahrestage). */
const occasionBase = (kind: OccasionKind): string => (kind === 'birthday' ? '/api/birthdays' : '/api/anniversaries')

/**
 * Dünner API-Client über `apiFetch` (lib/config): im Web same-origin per Cookie,
 * im nativen Build cross-origin gegen `API_BASE` mit Bearer-Token (ADR-042).
 * Antworten werden gegen die shared Zod-Schemas geparst → ein Contract für
 * Client und Server.
 */
async function json<T>(res: Response, schema: z.ZodType<T>): Promise<T> {
  if (!res.ok) throw new ApiError(res.status, await safeError(res))
  return schema.parse(await res.json())
}

async function safeError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string }
    return body.error ?? `HTTP ${res.status}`
  } catch {
    return `HTTP ${res.status}`
  }
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message)
  }
}

const membersResponse = z.object({ members: z.array(memberDtoSchema) })
const calendarsResponse = z.object({ calendars: z.array(calendarDtoSchema) })
const occurrencesResponse = z.object({ occurrences: z.array(occurrenceDtoSchema) })
const eventResponse = z.object({ event: eventDtoSchema })
const subscriptionsResponse = z.object({ subscriptions: z.array(calendarSubscriptionDtoSchema) })
const subscriptionResponse = z.object({ subscription: calendarSubscriptionDtoSchema })
const templatesResponse = z.object({ templates: z.array(taskTemplateDtoSchema) })
const templateResponse = z.object({ template: taskTemplateDtoSchema })
const pointsAccountsResponse = z.object({ accounts: z.array(pointsAccountDtoSchema) })
const pointsTxnsResponse = z.object({ transactions: z.array(pointsTxnDtoSchema) })
const shiftEntriesResponse = z.object({ entries: z.array(shiftPlanEntryDtoSchema) })
const shiftApplyResponse = z.object({ ok: z.boolean(), created: z.number().int(), updated: z.number().int() })
const calendarResponse = z.object({ calendar: calendarDtoSchema })
const listsResponse = z.object({ lists: z.array(listDtoSchema) })
const listResponse = z.object({ list: listDtoSchema })
const itemsResponse = z.object({ items: z.array(listItemDtoSchema) })
const itemResponse = z.object({ item: listItemDtoSchema })

async function send<T>(url: string, method: string, body: unknown, schema: z.ZodType<T>): Promise<T> {
  const res = await apiFetch(url, {
    method,
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
  return json(res, schema)
}

export const api = {
  async health(): Promise<HealthResponse> {
    return json(await apiFetch('/api/health'), healthResponseSchema)
  },

  // ── Auth ──
  async me(): Promise<MemberDto | null> {
    const res = await apiFetch('/api/auth/me')
    if (res.status === 401) return null
    return (await json(res, authMeResponseSchema)).member
  },
  async login(name: string, secret: string): Promise<MemberDto> {
    // Nativer Build fordert Token-Auth an (X-Client: native) → Token im Body,
    // der im Secure Storage landet. Web bleibt beim Cookie (kein Token/Header).
    const res = await apiFetch('/api/auth/login', {
      method: 'POST',
      headers: isNative
        ? { 'content-type': 'application/json', 'x-client': 'native' }
        : { 'content-type': 'application/json' },
      body: JSON.stringify(isNative ? { name, secret, tokenAuth: true } : { name, secret }),
    })
    const data = await json(res, loginResponseSchema)
    if (isNative && data.token) setToken(data.token)
    return data.member
  },
  async logout(): Promise<void> {
    await apiFetch('/api/auth/logout', { method: 'POST' })
    setToken(null)
  },

  // ── Anmeldung über Authentik (OIDC, ADR-082) ──
  // Rein optional: Ist der Server nicht konfiguriert, liefert /status
  // `enabled:false` und die Oberfläche zeigt gar nichts davon. Der Login per
  // Name + Passwort/PIN bleibt in jedem Fall der Hauptweg (Break-Glass).
  async oidcStatus(): Promise<OidcStatus> {
    return json(await apiFetch('/api/auth/oidc/status'), oidcStatusSchema)
  },
  async oidcDisconnect(): Promise<void> {
    await send('/api/auth/oidc/disconnect', 'POST', {}, z.object({ ok: z.boolean() }))
  },

  // ── Erst-Einrichtung ──
  async setupStatus(): Promise<boolean> {
    return (await json(await apiFetch('/api/setup/status'), setupStatusSchema)).needsSetup
  },
  async setup(payload: SetupRequest): Promise<MemberDto> {
    return (await send('/api/setup', 'POST', payload, authMeResponseSchema)).member
  },

  // ── Mitglieder ──
  async listMembers(): Promise<MemberDto[]> {
    return (await json(await apiFetch('/api/members'), membersResponse)).members
  },
  async createMember(payload: CreateMember): Promise<MemberDto> {
    return (await send('/api/members', 'POST', payload, z.object({ member: memberDtoSchema }))).member
  },
  async updateMember(id: string, payload: UpdateMember): Promise<MemberDto> {
    return (await send(`/api/members/${id}`, 'PATCH', payload, z.object({ member: memberDtoSchema }))).member
  },
  async deleteMember(id: string): Promise<void> {
    const res = await apiFetch(`/api/members/${id}`, { method: 'DELETE' })
    if (!res.ok) throw new ApiError(res.status, await safeError(res))
  },
  async uploadAvatar(memberId: string, file: File): Promise<MemberDto> {
    const fd = new FormData()
    fd.append('file', file)
    return (await json(await apiFetch(`/api/members/${memberId}/avatar`, { method: 'POST', body: fd }), z.object({ member: memberDtoSchema }))).member
  },
  async deleteAvatar(memberId: string): Promise<MemberDto> {
    return (await json(await apiFetch(`/api/members/${memberId}/avatar`, { method: 'DELETE' }), z.object({ member: memberDtoSchema }))).member
  },

  // ── Kalender ──
  async listCalendars(): Promise<CalendarDto[]> {
    return (await json(await apiFetch('/api/calendars'), calendarsResponse)).calendars
  },
  async createCalendar(payload: CreateCalendar): Promise<CalendarDto> {
    const res = await apiFetch('/api/calendars', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
    })
    return (await json(res, calendarResponse)).calendar
  },

  // ── Events ──
  async listOccurrences(fromISO: string, toISO: string): Promise<OccurrenceDto[]> {
    const url = `/api/events?from=${encodeURIComponent(fromISO)}&to=${encodeURIComponent(toISO)}`
    return (await json(await apiFetch(url), occurrencesResponse)).occurrences
  },
  async createEvent(payload: CreateEvent): Promise<EventDto> {
    const res = await apiFetch('/api/events', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
    })
    return (await json(res, eventResponse)).event
  },
  async updateEvent(id: string, payload: UpdateEvent): Promise<EventDto> {
    const res = await apiFetch(`/api/events/${id}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
    })
    return (await json(res, eventResponse)).event
  },
  /**
   * Termin löschen. Bei Serien optional nur ein Vorkommen bzw. dieses und alle
   * folgenden (ADR-087); ohne scope wie bisher die ganze Serie.
   */
  async deleteEvent(id: string, opts?: { scope: SeriesScope; occurrenceStart: string }): Promise<void> {
    const q = opts && opts.scope !== 'all' ? `?scope=${opts.scope}&occurrenceStart=${encodeURIComponent(opts.occurrenceStart)}` : ''
    const res = await apiFetch(`/api/events/${id}${q}`, { method: 'DELETE' })
    if (!res.ok) throw new ApiError(res.status, await safeError(res))
  },
  /** Zuletzt verwendete Orte der Familie (Autocomplete, ADR-090). */
  async eventLocations(): Promise<string[]> {
    return (await json(await apiFetch('/api/events/locations'), z.object({ locations: z.array(z.string()) }))).locations
  },

  // ── Kalender-Feed (ICS-Export, ADR-086) ──
  async calendarFeedStatus(): Promise<CalendarFeedStatus> {
    return json(await apiFetch('/api/calendar/feed-token'), calendarFeedStatusSchema)
  },
  async createCalendarFeed(): Promise<CalendarFeedCreated> {
    return send('/api/calendar/feed-token', 'POST', {}, calendarFeedCreatedSchema)
  },
  async revokeCalendarFeed(): Promise<void> {
    const res = await apiFetch('/api/calendar/feed-token', { method: 'DELETE' })
    if (!res.ok) throw new ApiError(res.status, await safeError(res))
  },

  // ── Externe iCal-Abos (Müll/Feiertage/Ferien) ──
  async listSubscriptionPresets(): Promise<CalendarPreset[]> {
    return (await json(await apiFetch('/api/calendar-subscriptions/presets'), z.object({ presets: z.array(calendarPresetSchema) }))).presets
  },
  async listSubscriptions(): Promise<CalendarSubscriptionDto[]> {
    return (await json(await apiFetch('/api/calendar-subscriptions'), subscriptionsResponse)).subscriptions
  },
  async createSubscription(payload: CreateCalendarSubscription): Promise<CalendarSubscriptionDto> {
    return (await send('/api/calendar-subscriptions', 'POST', payload, subscriptionResponse)).subscription
  },
  async updateSubscription(id: string, payload: UpdateCalendarSubscription): Promise<CalendarSubscriptionDto> {
    return (await send(`/api/calendar-subscriptions/${id}`, 'PATCH', payload, subscriptionResponse)).subscription
  },
  async syncSubscription(id: string): Promise<CalendarSubscriptionDto> {
    return (await send(`/api/calendar-subscriptions/${id}/sync`, 'POST', {}, subscriptionResponse)).subscription
  },
  async deleteSubscription(id: string): Promise<void> {
    const res = await apiFetch(`/api/calendar-subscriptions/${id}`, { method: 'DELETE' })
    if (!res.ok) throw new ApiError(res.status, await safeError(res))
  },

  // ── Aufgaben-Vorlagen (Punkte-Aufgaben) ──
  async listTaskTemplates(): Promise<TaskTemplateDto[]> {
    return (await json(await apiFetch('/api/task-templates'), templatesResponse)).templates
  },
  async createTaskTemplate(payload: CreateTaskTemplate): Promise<TaskTemplateDto> {
    return (await send('/api/task-templates', 'POST', payload, templateResponse)).template
  },
  async updateTaskTemplate(id: string, payload: UpdateTaskTemplate): Promise<TaskTemplateDto> {
    return (await send(`/api/task-templates/${id}`, 'PATCH', payload, templateResponse)).template
  },
  async deleteTaskTemplate(id: string): Promise<void> {
    const res = await apiFetch(`/api/task-templates/${id}`, { method: 'DELETE' })
    if (!res.ok) throw new ApiError(res.status, await safeError(res))
  },
  /** Sofort eine Instanz für heute in „Zu erledigen" erzeugen. Gibt Anzahl neu erzeugter zurück. */
  async instantiateTaskTemplate(id: string): Promise<number> {
    const res = await apiFetch(`/api/task-templates/${id}/instantiate`, { method: 'POST' })
    if (!res.ok) throw new ApiError(res.status, await safeError(res))
    return ((await res.json()) as { created?: number }).created ?? 0
  },
  /** Aufgabe nachtragen: einem Kind eine bereits erledigte Aufgabe direkt gutschreiben (Punkte + Feed). */
  async creditTask(payload: CreditTask): Promise<{ credited: number; balance: number }> {
    const res = await apiFetch('/api/task-credits', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(payload) })
    if (!res.ok) throw new ApiError(res.status, await safeError(res))
    return (await res.json()) as { credited: number; balance: number }
  },

  // ── Punktekonto ──
  async listPointsAccounts(): Promise<PointsAccountDto[]> {
    return (await json(await apiFetch('/api/points/accounts'), pointsAccountsResponse)).accounts
  },
  async listPointsTransactions(accountId: string): Promise<PointsTxnDto[]> {
    return (await json(await apiFetch(`/api/points/accounts/${accountId}/transactions`), pointsTxnsResponse)).transactions
  },
  async payoutPoints(accountId: string): Promise<void> {
    const res = await apiFetch(`/api/points/accounts/${accountId}/payout`, { method: 'POST' })
    if (!res.ok) throw new ApiError(res.status, await safeError(res))
  },
  async requestPayout(accountId: string): Promise<void> {
    const res = await apiFetch(`/api/points/accounts/${accountId}/request-payout`, { method: 'POST' })
    if (!res.ok) throw new ApiError(res.status, await safeError(res))
  },

  // ── Dienstplan (nur Eltern) ──
  async listShiftPlan(month: string): Promise<ShiftPlanEntryDto[]> {
    return (await json(await apiFetch(`/api/shift-plan?month=${month}`), shiftEntriesResponse)).entries
  },
  async setShift(date: string, code: ShiftCode | null): Promise<void> {
    await send('/api/shift-plan', 'PUT', { date, code }, z.object({ ok: z.boolean() }))
  },
  async applyShiftPlan(month: string): Promise<{ created: number; updated: number }> {
    return send('/api/shift-plan/apply', 'POST', { month }, shiftApplyResponse)
  },

  // ── Listen ──
  async listLists(): Promise<ListDto[]> {
    return (await json(await apiFetch('/api/lists'), listsResponse)).lists
  },
  async createList(payload: CreateList): Promise<ListDto> {
    return (await send('/api/lists', 'POST', payload, listResponse)).list
  },
  async updateList(id: string, payload: UpdateList): Promise<ListDto> {
    return (await send(`/api/lists/${id}`, 'PATCH', payload, listResponse)).list
  },
  async deleteList(id: string): Promise<void> {
    const res = await apiFetch(`/api/lists/${id}`, { method: 'DELETE' })
    if (!res.ok) throw new ApiError(res.status, await safeError(res))
  },
  async listItems(listId: string): Promise<ListItemDto[]> {
    return (await json(await apiFetch(`/api/lists/${listId}/items`), itemsResponse)).items
  },
  async addItem(listId: string, payload: CreateListItem): Promise<ListItemDto> {
    return (await send(`/api/lists/${listId}/items`, 'POST', payload, itemResponse)).item
  },
  async updateItem(id: string, payload: UpdateListItem): Promise<ListItemDto> {
    return (await send(`/api/list-items/${id}`, 'PATCH', payload, itemResponse)).item
  },
  async deleteItem(id: string): Promise<void> {
    const res = await apiFetch(`/api/list-items/${id}`, { method: 'DELETE' })
    if (!res.ok) throw new ApiError(res.status, await safeError(res))
  },

  // ── Feed ──
  async getFeed(cursor?: string | null): Promise<FeedPage> {
    const url = cursor ? `/api/feed?cursor=${encodeURIComponent(cursor)}` : '/api/feed'
    return json(await apiFetch(url), feedPageSchema)
  },
  async feedUnread(): Promise<number> {
    return (await json(await apiFetch('/api/feed/unread'), z.object({ count: z.number() }))).count
  },
  async feedMarkSeen(): Promise<void> {
    await send('/api/feed/seen', 'POST', {}, z.object({ ok: z.boolean() }))
  },
  async createPost(body: string): Promise<FeedActivityDto> {
    return (await send('/api/feed', 'POST', { body }, z.object({ activity: feedActivityDtoSchema }))).activity
  },
  async addComment(id: string, body: string): Promise<FeedCommentDto> {
    return (await send(`/api/feed/${id}/comments`, 'POST', { body }, z.object({ comment: feedCommentDtoSchema }))).comment
  },
  async toggleLike(id: string): Promise<{ liked: boolean; likeCount: number }> {
    return send(`/api/feed/${id}/like`, 'POST', {}, z.object({ liked: z.boolean(), likeCount: z.number() }))
  },
  async deleteActivity(id: string): Promise<void> {
    const res = await apiFetch(`/api/feed/${id}`, { method: 'DELETE' })
    if (!res.ok) throw new ApiError(res.status, await safeError(res))
  },

  // ── Abstimmungen im Feed (ADR-081) ──
  async createPoll(payload: CreatePoll): Promise<void> {
    await send('/api/feed/polls', 'POST', payload, z.object({ ok: z.boolean() }))
  },
  async votePoll(pollId: string, optionIds: string[]): Promise<void> {
    await send(`/api/feed/polls/${pollId}/vote`, 'POST', { optionIds }, z.object({ ok: z.boolean() }))
  },
  async closePoll(pollId: string): Promise<void> {
    await send(`/api/feed/polls/${pollId}/close`, 'POST', {}, z.object({ ok: z.boolean() }))
  },

  // ── Chat ──
  async listConversations(): Promise<ConversationDto[]> {
    return (await json(await apiFetch('/api/conversations'), z.object({ conversations: z.array(conversationDtoSchema) }))).conversations
  },
  async createConversation(payload: CreateConversation): Promise<ConversationDto> {
    return (await send('/api/conversations', 'POST', payload, z.object({ conversation: conversationDtoSchema }))).conversation
  },
  async renameConversation(id: string, name: string): Promise<ConversationDto> {
    return (await send(`/api/conversations/${id}`, 'PATCH', { name }, z.object({ conversation: conversationDtoSchema }))).conversation
  },
  async addConversationMembers(id: string, memberIds: string[]): Promise<ConversationDto> {
    return (await send(`/api/conversations/${id}/members`, 'POST', { memberIds }, z.object({ conversation: conversationDtoSchema }))).conversation
  },
  async removeConversationMember(id: string, memberId: string): Promise<void> {
    const res = await apiFetch(`/api/conversations/${id}/members/${memberId}`, { method: 'DELETE' })
    if (!res.ok) throw new ApiError(res.status, await safeError(res))
  },
  async listMessages(convId: string): Promise<MessageDto[]> {
    return (await json(await apiFetch(`/api/conversations/${convId}/messages`), z.object({ messages: z.array(messageDtoSchema) }))).messages
  },
  async sendMessage(convId: string, body: string): Promise<MessageDto> {
    return (await send(`/api/conversations/${convId}/messages`, 'POST', { body }, z.object({ message: messageDtoSchema }))).message
  },
  async uploadAttachment(convId: string, file: File): Promise<MessageDto> {
    const fd = new FormData()
    fd.append('file', file)
    return (await json(await apiFetch(`/api/conversations/${convId}/attachments`, { method: 'POST', body: fd }), z.object({ message: messageDtoSchema }))).message
  },
  /** Anhang auth-gesichert laden (Cookie im Web, Bearer nativ) → Object-URL. Aufrufer gibt sie mit URL.revokeObjectURL frei. */
  async attachmentObjectUrl(messageId: string): Promise<string> {
    const res = await apiFetch(`/api/messages/${messageId}/attachment`)
    if (!res.ok) throw new ApiError(res.status, await safeError(res))
    return URL.createObjectURL(await res.blob())
  },
  async markRead(convId: string): Promise<void> {
    await send(`/api/conversations/${convId}/read`, 'POST', {}, z.object({ ok: z.boolean() }))
  },
  async deleteMessage(id: string): Promise<void> {
    const res = await apiFetch(`/api/messages/${id}`, { method: 'DELETE' })
    if (!res.ok) throw new ApiError(res.status, await safeError(res))
  },

  // ── Taschengeld ──
  async listAccounts(): Promise<AllowanceAccountDto[]> {
    return (await json(await apiFetch('/api/allowance/accounts'), z.object({ accounts: z.array(allowanceAccountDtoSchema) }))).accounts
  },
  async createAccount(memberId: string): Promise<AllowanceAccountDto> {
    return (await send('/api/allowance/accounts', 'POST', { memberId }, z.object({ account: allowanceAccountDtoSchema }))).account
  },
  async setOverdraft(accountId: string, overdraftLimitCents: number): Promise<AllowanceAccountDto> {
    return (await send(`/api/allowance/accounts/${accountId}/overdraft`, 'PATCH', { overdraftLimitCents }, z.object({ account: allowanceAccountDtoSchema }))).account
  },
  async listTransactions(accountId: string): Promise<AllowanceTxnDto[]> {
    return (await json(await apiFetch(`/api/allowance/accounts/${accountId}/transactions`), z.object({ transactions: z.array(allowanceTxnDtoSchema) }))).transactions
  },
  async bookTransaction(accountId: string, payload: CreateTxn): Promise<AllowanceTxnDto> {
    return (await send(`/api/allowance/accounts/${accountId}/transactions`, 'POST', payload, z.object({ transaction: allowanceTxnDtoSchema }))).transaction
  },
  async getRule(accountId: string): Promise<AllowanceRuleDto | null> {
    return (await json(await apiFetch(`/api/allowance/accounts/${accountId}/rule`), z.object({ rule: allowanceRuleDtoSchema.nullable() }))).rule
  },
  async upsertRule(accountId: string, payload: UpsertRule): Promise<AllowanceRuleDto> {
    return (await send(`/api/allowance/accounts/${accountId}/rule`, 'PUT', payload, z.object({ rule: allowanceRuleDtoSchema }))).rule
  },
  async deleteRule(accountId: string): Promise<void> {
    const res = await apiFetch(`/api/allowance/accounts/${accountId}/rule`, { method: 'DELETE' })
    if (!res.ok) throw new ApiError(res.status, await safeError(res))
  },
  async listGoals(accountId: string): Promise<SavingsGoalDto[]> {
    return (await json(await apiFetch(`/api/allowance/accounts/${accountId}/goals`), z.object({ goals: z.array(savingsGoalDtoSchema) }))).goals
  },
  async createGoal(accountId: string, payload: CreateGoal): Promise<SavingsGoalDto> {
    return (await send(`/api/allowance/accounts/${accountId}/goals`, 'POST', payload, z.object({ goal: savingsGoalDtoSchema }))).goal
  },
  async updateGoal(goalId: string, payload: UpdateGoal): Promise<SavingsGoalDto> {
    return (await send(`/api/allowance/goals/${goalId}`, 'PATCH', payload, z.object({ goal: savingsGoalDtoSchema }))).goal
  },
  async deleteGoal(goalId: string): Promise<void> {
    const res = await apiFetch(`/api/allowance/goals/${goalId}`, { method: 'DELETE' })
    if (!res.ok) throw new ApiError(res.status, await safeError(res))
  },
  async listGradeRewards(): Promise<GradeRewardDto[]> {
    return (await json(await apiFetch('/api/allowance/grade-rewards'), z.object({ rewards: z.array(gradeRewardDtoSchema) }))).rewards
  },
  async updateGradeRewards(rewards: GradeRewardDto[]): Promise<GradeRewardDto[]> {
    return (await send('/api/allowance/grade-rewards', 'PUT', { rewards }, z.object({ rewards: z.array(gradeRewardDtoSchema) }))).rewards
  },
  async getBankConfig(): Promise<BankConfigDto> {
    return (await json(await apiFetch('/api/allowance/bank-config'), z.object({ config: bankConfigDtoSchema }))).config
  },
  async updateBankConfig(config: BankConfigDto): Promise<BankConfigDto> {
    return (await send('/api/allowance/bank-config', 'PUT', config, z.object({ config: bankConfigDtoSchema }))).config
  },
  async bookGradeBonus(accountId: string, payload: GradeBonus): Promise<AllowanceTxnDto> {
    return (await send(`/api/allowance/accounts/${accountId}/grade-bonus`, 'POST', payload, z.object({ transaction: allowanceTxnDtoSchema }))).transaction
  },

  // ── Integrationen (Immich / Tandoor) ──
  async integrationsStatus(): Promise<{ immich: boolean; tandoor: boolean }> {
    return json(await apiFetch('/api/integrations/status'), integrationsStatusSchema)
  },
  async tandoorMealPlan(): Promise<TandoorMealDto[]> {
    return (await json(await apiFetch('/api/integrations/tandoor/meal-plan'), z.object({ meals: z.array(tandoorMealDtoSchema) }))).meals
  },
  async tandoorSyncShopping(): Promise<TandoorSyncResult> {
    return send('/api/integrations/tandoor/sync-shopping', 'POST', {}, tandoorSyncResultSchema)
  },
  async tandoorRecipes(query: string, page = 1): Promise<{ recipes: TandoorRecipeDto[]; hasMore: boolean }> {
    const url = `/api/integrations/tandoor/recipes?query=${encodeURIComponent(query)}&page=${page}`
    return json(await apiFetch(url), z.object({ recipes: z.array(tandoorRecipeDtoSchema), hasMore: z.boolean() }))
  },
  async tandoorWishes(): Promise<MealWishDto[]> {
    return (await json(await apiFetch('/api/integrations/tandoor/wishes'), z.object({ wishes: z.array(mealWishDtoSchema) }))).wishes
  },
  async tandoorAddWish(recipeId: number): Promise<void> {
    await send('/api/integrations/tandoor/wishes', 'POST', { recipeId }, z.object({ wish: mealWishDtoSchema.nullable() }))
  },
  async tandoorRemoveWish(id: string): Promise<void> {
    const res = await apiFetch(`/api/integrations/tandoor/wishes/${id}`, { method: 'DELETE' })
    if (!res.ok) throw new ApiError(res.status, await safeError(res))
  },
  async tandoorFulfillWish(id: string, fulfilled: boolean): Promise<void> {
    await send(`/api/integrations/tandoor/wishes/${id}`, 'PATCH', { fulfilled }, z.object({ wish: mealWishDtoSchema }))
  },
  async immichAlbums(): Promise<ImmichAlbumDto[]> {
    return (await json(await apiFetch('/api/integrations/immich/albums'), z.object({ albums: z.array(immichAlbumDtoSchema) }))).albums
  },
  async immichManageAlbums(): Promise<ImmichManageAlbumDto[]> {
    return (await json(await apiFetch('/api/integrations/immich/manage'), z.object({ albums: z.array(immichManageAlbumDtoSchema) }))).albums
  },
  async immichHideAlbum(albumId: string): Promise<void> {
    await send('/api/integrations/immich/hidden', 'POST', { immichAlbumId: albumId }, z.object({ ok: z.boolean() }))
  },
  async immichUnhideAlbum(albumId: string): Promise<void> {
    const res = await apiFetch(`/api/integrations/immich/hidden/${albumId}`, { method: 'DELETE' })
    if (!res.ok) throw new ApiError(res.status, await safeError(res))
  },
  async immichAlbumAssets(albumId: string): Promise<{ album: ImmichAlbumDto; assets: ImmichAssetDto[] }> {
    return json(await apiFetch(`/api/integrations/immich/albums/${albumId}/assets`), z.object({ album: immichAlbumDtoSchema, assets: z.array(immichAssetDtoSchema) }))
  },

  // ── Anlässe (Geburtstage / Jahrestage) ──
  async listOccasions(kind: OccasionKind): Promise<BirthdayDto[]> {
    return (await json(await apiFetch(occasionBase(kind)), z.object({ birthdays: z.array(birthdayDtoSchema) }))).birthdays
  },
  async createOccasion(kind: OccasionKind, payload: CreateBirthday): Promise<BirthdayDto> {
    return (await send(occasionBase(kind), 'POST', payload, z.object({ birthday: birthdayDtoSchema }))).birthday
  },
  async updateOccasion(kind: OccasionKind, id: string, payload: UpdateBirthday): Promise<BirthdayDto> {
    return (await send(`${occasionBase(kind)}/${id}`, 'PATCH', payload, z.object({ birthday: birthdayDtoSchema }))).birthday
  },
  async deleteOccasion(kind: OccasionKind, id: string): Promise<void> {
    const res = await apiFetch(`${occasionBase(kind)}/${id}`, { method: 'DELETE' })
    if (!res.ok) throw new ApiError(res.status, await safeError(res))
  },

  // ── Web-Push ──
  async pushVapidKey(): Promise<string | null> {
    return (await json(await apiFetch('/api/push/vapid-public-key'), z.object({ key: z.string().nullable() }))).key
  },
  async pushSubscribe(sub: { endpoint: string; keys: { p256dh: string; auth: string }; deviceLabel?: string }): Promise<void> {
    await send('/api/push/subscribe', 'POST', sub, z.object({ ok: z.boolean() }))
  },
  async pushUnsubscribe(endpoint: string): Promise<void> {
    await send('/api/push/unsubscribe', 'POST', { endpoint }, z.object({ ok: z.boolean() }))
  },
  async pushRegisterNative(body: { token: string; platform: 'android' | 'ios'; deviceLabel?: string }): Promise<void> {
    await send('/api/push/register-native', 'POST', body, z.object({ ok: z.boolean() }))
  },

  // ── App-Updates (ADR-051) ──
  async latestRelease(): Promise<LatestReleaseResponse> {
    return json(await apiFetch('/api/app/latest?platform=android'), latestReleaseResponseSchema)
  },
  async uploadRelease(file: File, notes: string): Promise<AppReleaseDto> {
    const fd = new FormData()
    fd.append('file', file)
    const qs = `notes=${encodeURIComponent(notes)}`
    return (await json(await apiFetch(`/api/app/releases?${qs}`, { method: 'POST', body: fd }), z.object({ release: appReleaseDtoSchema }))).release
  },
  async listReleases(): Promise<AppReleaseDto[]> {
    return (await json(await apiFetch('/api/app/releases'), releaseListResponseSchema)).releases
  },
  async publishRelease(id: string): Promise<AppReleaseDto> {
    return (await send(`/api/app/releases/${id}/publish`, 'POST', {}, z.object({ release: appReleaseDtoSchema }))).release
  },
  async deleteRelease(id: string): Promise<void> {
    const res = await apiFetch(`/api/app/releases/${id}`, { method: 'DELETE' })
    if (!res.ok) throw new ApiError(res.status, await safeError(res))
  },
  /** APK auth-gesichert laden → Blob (für Installer nativ / Download im Web). */
  async releaseApkBlob(id: string): Promise<Blob> {
    const res = await apiFetch(`/api/app/releases/${id}/apk`)
    if (!res.ok) throw new ApiError(res.status, await safeError(res))
    return res.blob()
  },
}
